import { useTranslations } from 'next-intl'
import styles from './step2.module.css'
import OTP from '@/components/UI/OTP/OTP'
import { useValidatePasswordCode } from '@/apiUtilities/mutations/useValidatePasswordCode'
import { ForgotPasswordSteps } from '../page'
import Button, { ButtonKind, ButtonSize } from '@/components/UI/Buttons/Button/Button'
import { useRequestPasswordCode } from '@/apiUtilities/mutations/useRequestPasswordCode'
import { toBase64URI } from '@/utils/helpers'
import { useEffect, useRef, useState } from 'react'
import { API_Error } from '@/interfaces/api/response/api_response_types'
import { toast } from 'react-toastify'
import Notification from '@/components/Notification/Notification'

interface ForgotPasswordStep2Props {
	username: string,
	setStep: (step: ForgotPasswordSteps) => void, 
	setCode: (code: string | null) => void
}

const START_TIMER = 16
const CODE_LENGTH = 5

export default function ForgotPassword_Step2 ({ username, setStep, setCode } : ForgotPasswordStep2Props) {
	const t = useTranslations('ForgotPassword')
	const validatePassword = useValidatePasswordCode()
	const requestPasswordCode = useRequestPasswordCode()
	const [resendAvailable, setResendAvailable] = useState<boolean>(true)
	const [timer, setTimer] = useState<number>(START_TIMER)
	const timerInterval = useRef<number | null>(null)
	const [error, setError] = useState<boolean>(false)

	/**
	 * Handles OTP input changes.
	 * 
	 * When the user has entered all required digits (CODE_LENGTH),
	 * it triggers the password code validation mutation
	 * 
	 * On success, it moves the flow to STEP_3 and stores the code
	 * On error, it sets the local error state to display feedback in the OTP component
	 * 
	 * @param value - The full OTP value entered by the user
	 */
	function handleChange(value: string) {
		if(error) setError(false)

		if(value.length === CODE_LENGTH) {
			validatePassword.mutate({ userName: username, code: value}, {
				onSuccess: () => {
					setStep(ForgotPasswordSteps.STEP_3)
					setCode(value)
				},
				onError: (response: API_Error) => {
					setError(true)
				}
			})
		}   
	}

	/**
	 * Handles the resend code action
	 * 
	 * Prevents the default button behavior and triggers
	 * a new password code request mutation
	 * 
	 * When successful, it disables the resend button and starts the countdown timer
	 * 
	 * @param e - Mouse click event triggered by the resend button
	 */
	function handleResend(e: React.MouseEvent<HTMLButtonElement>) {
		e.preventDefault()
		requestPasswordCode.mutate(toBase64URI(username), {
			onSuccess: () => {
				setResendAvailable(false)
			},
			onError: (error) => {
				toast.error(<Notification text={t('messages.server.step2ResendCodeFail')} />)
			}
		})
	}

	/**
	 * Handles the timer for resending the password code
	 */
	useEffect(() => {
		if (!resendAvailable) {
			if (timerInterval.current) window.clearInterval(timerInterval.current)

			timerInterval.current = window.setInterval(() => {
				setTimer(prev => prev - 1)
			}, 1000)
		}

		return () => {
			if(timerInterval.current !== null) window.clearInterval(timerInterval.current)
		}
	}, [resendAvailable])

	/**
	 * Resets the timer and makes the resend button available again when the timer reaches 0
	 */
	useEffect(() => {
		if(timer === 0) {
			setResendAvailable(true)
			setTimer(START_TIMER)

			if(timerInterval.current) {
				window.clearInterval(timerInterval.current)
				timerInterval.current = null
			}
		}
	}, [timer])

	return (
		<form className={styles.main} data-testid='Step2'>
			<div className={styles.contentWrapper}>
				<p data-testid='Step2_description' className={'bodyTextLarge'}>{t('step2Description', { username })}</p>
				<OTP numberOfInputs={CODE_LENGTH} numeric={true} onChange={handleChange} error={error}/>
			</div>

			<div className={styles.sendWrapper}>
				{resendAvailable && <p data-testid='Step2_resendText' className={`bodyTextMedium ${styles.resendTxt}`}>{t('resendText')}</p>}
				{!resendAvailable && <p data-testid='Step1_resendTimer' className={`bodyTextMedium ${styles.resendTxt}`}>{t('resendCodeTimer')}<span className={styles.timer}>00:{String(timer).padStart(2, '0')}</span></p>}
				<Button data-testid='Step2_button' type={"button"} disabled={!resendAvailable} size={ButtonSize.MEDIUM} kind={ButtonKind.PRIMARY} className={styles.resendBtn} onClick={handleResend}>{t('resendCode')}</Button>
			</div>
		</form>
	)
}